import { UNAVAILABLE_WHILE_RECORDING } from "@cuekards/core/refresh";

/**
 * Which screen a tab is on, and where a load that finished lands (web-ui ticket
 * 07; narrowed by flow ticket 02).
 *
 * A module of its own, beside `control-bar.ts`, `green-room.ts` and `refresh.ts`,
 * and for the reason those are: {@link settle} is a rule about a hazard nobody
 * would find by looking at the screen, and a rule that arrived with a bug is
 * exactly the sort that ought to be checkable without mounting a browser.
 *
 * **Three screens rather than four since flow ticket 02.** `help` was a branch
 * here, carrying `from: Stage` — the one recursive member of this union — and it
 * went with `HelpPage.tsx`. How it works is a link to `/how-it-works` in a tab of
 * its own now, which is not a stage at all: one rendering of the walkthrough for
 * a reader instead of two, and no screen for a load to resolve underneath.
 */

/**
 * Which screen is up, and what the green room is saying.
 *
 * `setup` kept its name through web-ui ticket 05 rather than becoming `room`. It
 * is what the screen *is* — the place a script gets loaded and a camera gets
 * pointed — and renaming it would have touched every branch in `WebApp.tsx` for a
 * word, in a ticket that already rewrote the thing it names.
 */
export type Stage =
  | { kind: "setup"; loading: boolean; error?: string }
  | { kind: "prompting" }
  /** Settings, and where "Done" goes back to — which is wherever it was opened
   * from, because it is reachable from both of the other two. */
  | { kind: "settings"; from: "setup" | "prompting" };

/** Nothing loading, nothing wrong, no script — where a tab starts and what
 * clearing one goes back to. */
export const IDLE: Stage = { kind: "setup", loading: false };

/**
 * Where a load's outcome lands, which is not always the screen in front of you.
 *
 * A load is a network round trip, and every button on the screen is live for the
 * whole of it: press Load, press something, and the screen you asked for is
 * replaced by the one the fetch resolved into — on a shared `?doc=` arrival, a
 * full-screen prompter nobody asked for.
 *
 * **The `?` was the sharp version of that and is no longer a screen** (flow
 * ticket 02). It opened a help page that could close itself a second later, and
 * the branch that held it up carried the load's outcome along behind it so
 * `← Back` landed on the script that had just arrived. All of that went with
 * `HelpPage`: a link in a tab of its own cannot be resolved out from under
 * anybody.
 *
 * **What is left is the settings screen, and it cannot carry the outcome**,
 * because its `from` is the name of a screen rather than a screen. An error
 * message is dropped there — which is what happened before web-ui ticket 07 too,
 * and is a smaller wrong than either of the alternatives: throwing somebody out
 * of the settings they opened, or teaching `settings.from` to be a `Stage` on the
 * way past.
 *
 * **And the prompter** (mobile-app ticket 35). A load starts in the room, so the
 * prompter is only up when it lands if a take opened it mid-fetch — `R`, or a
 * spoken start — and the room drawn over a rolling take is a reader looking at
 * the wrong screen for the whole of it. The load itself is refused by then, so
 * nothing is lost but a sentence about the take said while the take is on.
 *
 * One branch and a fall-through is a thin function, and it stays a function
 * rather than folding back into `load`: it is the only place in the page that
 * knows a finished load must not move somebody who has stepped off the room, and
 * `stage.test.ts` is what says so.
 */
export function settle(outcome: Stage): (current: Stage) => Stage {
  return (current) =>
    current.kind === "settings" || current.kind === "prompting" ? current : outcome;
}

/**
 * The room without the take's sentence, once the take is over (mobile-app
 * ticket 35).
 *
 * A load refused because a take was on says {@link UNAVAILABLE_WHILE_RECORDING}
 * in the room — pressed mid-take, or asked for at `idle` and still fetching when
 * `R` was pressed. That is true for exactly as long as the take is, and over a
 * room where nothing is recording it is a reason not to press a control that now
 * works. Any other failure stays: the take did not cause it and its end does not
 * mend it. The phone's room drops the same sentence the same way (ticket 26).
 */
export function afterTheTake(current: Stage): Stage {
  if (current.kind !== "setup" || current.error !== UNAVAILABLE_WHILE_RECORDING) {
    return current;
  }
  return { kind: "setup", loading: current.loading };
}
